import React from 'react'
import { PageTitle } from '_metronic/layout/core'

import { ClientSideTable } from 'Presentation/Components/Table'
import FormGenerator from 'Presentation/Components/Form/FormGenerator'

import AddItemTypeSelection from '../Components/AddItemTypeSelection'
import QuotationURL from '../Components/QuotationURL'
import DocumentQuotationFormProductAddFormView from '../ProductAddForm/View'

import useViewModel from './ViewModel'
import formatNumberCommas from 'Utils/formatNumberCommas'
import clsx from 'clsx'

//////////
// View //
//////////

const DocumentQuotationFormView: React.FC = () => {
	const {
		currentLanguage,
		canGenerateQuotation,
		canSelectProductLine,
		canEditQuotation,
		isAccepted,
		isSelectingProduct,
		setIsSelectingProduct,
		documentStatusStyles,
		pageTitle,
		pageBreachCrumbs,
		items,
		financial,
		net,
		netBeforeVat,
		vatAmount,
		acceptedBuyer,
		isLoading,
		isSubmitting,
		selectedProduct,
		selectingProductFormType,
		generatedUrl,
		onChatBuyer,
		onChangeDiscount,
		onChangeVatCalculated,
		onAddNewItem,
		onBackFromSelectNewItem,
		onClickAddNewItem,
		onConfirmAddProduct,
		onGenerateQuotationForPublic,
		onBack,
		onSubmit,
		TABLE_COLUMNS,
	} = useViewModel()

	return (
		<ViewPageTitle pageBreadCrumb={pageBreachCrumbs} pageTitle={pageTitle}>
			<AddItemTypeSelection onSelectItemType={onAddNewItem} />
			<QuotationURL url={generatedUrl} />

			<div className="row gy-5">
				<div className="col-12">
					<div className="card border-kumopack-grey-200">
						<div className="card-header">
							<div className="card-title"></div>
							<div className="card-toolbar">
								<button
									className={documentStatusStyles.className}
									style={{ pointerEvents: 'none' }}>
									{documentStatusStyles.title}
								</button>
							</div>
						</div>

						<div className="card-body">
							<div className="row gy-5">
								{isSelectingProduct && (
									<DocumentQuotationFormProductAddFormView
										canSelectProductLine={canSelectProductLine}
										formType={selectingProductFormType}
										totalItems={items.length}
										selectedProduct={selectedProduct}
										isSelectingProduct={isSelectingProduct}
										setSelectingProduct={setIsSelectingProduct}
										onConfirmAddProduct={onConfirmAddProduct}
										onBack={onBackFromSelectNewItem}
									/>
								)}

								{!isSelectingProduct && (
									<React.Fragment>
										{isAccepted && (
											<React.Fragment>
												<div className="card border border-kumopack-grey-200 mb-5">
													<div className="card-body">
														<div className="row gy-10 align-items-center ">
															<div className="col-12 col-xl-1 text-center">
																<img
																	src={
																		acceptedBuyer?.companyLogo ??
																		'/media/icons/kumopack/default-placeholder.png'
																	}
																	onError={e => {
																		e.currentTarget.src =
																			'/media/icons/kumopack/default-placeholder.png'
																		e.currentTarget.onerror = null
																	}}
																	alt="Factory Profile"
																	width={64}
																	height={64}
																	style={{ borderRadius: '8px', objectFit: 'cover' }}
																/>
															</div>

															<div className="col-12 col-xl-6 px-xl-6">
																<div className="fw-bold fs-4 text-kumopack-grey-900">
																	{currentLanguage === 'en'
																		? "Accepted Buyer's Name"
																		: 'ชื่อผู้ซื้อ'}
																</div>
																<div className="text-kumopack-grey-600 text-center text-xl-start">
																	{currentLanguage === 'en'
																		? acceptedBuyer?.fullnameEn
																		: acceptedBuyer?.fullnameTh}{' '}
																	({acceptedBuyer?.code})
																</div>
															</div>

															<div className="col-12 col-xl-5">
																<div
																	className="d-flex flex-column flex-xl-row align-items-center justify-content-end"
																	style={{ gap: '12px' }}>
																	<button
																		className="btn btn-sm btn-kumopack-primary-600 text-kumopack-base-white fw-bold w-sm-100 w-md-100"
																		disabled={isSubmitting}
																		onClick={onChatBuyer}>
																		<img
																			src="/media/icons/kumopack/message-chat-circle-white.svg"
																			alt="chat icon"
																		/>{' '}
																		{currentLanguage === 'en' ? 'Chat' : 'แชทกับบริษัท'}
																	</button>
																</div>
															</div>
														</div>
													</div>
												</div>
											</React.Fragment>
										)}

										{!isAccepted && (
											<React.Fragment>
												<div className="col-12">
													<div className="alert alert-primary bg-kumopack-primary-50 border-kumopack-primary-700">
														<div className="alert-text fw-bold fs-5 text-kumopack-primary-700">
															ข้อแนะนำ : ราคาของสินค้าที่กรอกในแบบฟอร์ม ต้องเป็นราคาสินค้าต่อหน่วย
															และไม่รวมภาษี
														</div>
													</div>
												</div>

												<div className="col-12">
													<div className="alert alert-primary bg-kumopack-secondary-50 border-kumopack-secondary-700">
														<div className="alert-text fw-bold fs-5 text-kumopack-secondary-700">
															สิ่งที่ควรรู้ : ใบเสนอราคาสามารถเลือกประเภทของสินค้า (Product Line)
															ได้เพียง 1 ประเภทเท่านั้น
														</div>
													</div>
												</div>
											</React.Fragment>
										)}

										<div className="col-12">
											<div className="card border border-kumopack-grey-200">
												<div className="card-header">
													<div className="card-title">
														<h4
															className="d-inline-block text-kumopack-grey-900 fw-bold me-2"
															style={{ marginInline: 0, marginBlock: 0 }}>
															All Items
														</h4>
														<span
															className="d-inline-block badge badge-kumopack-secondary-50 text-kumopack-secondary-700"
															style={{ borderRadius: '16px' }}>
															{items.length} items
														</span>
													</div>
												</div>

												{/* Price Table */}
												<div className="card-body p-0">
													<ClientSideTable
														isLoading={false}
														data={items}
														columns={TABLE_COLUMNS}
														items_per_page={9999}
														customNoMatchText="ไม่มีรายการสินค้า"
													/>

													<hr
														style={{
															background: 'transparent',
															borderBottom: '1px solid #CCC',
															borderTop: 0,
															borderLeft: 0,
															borderRight: 0,
															margin: 0,
														}}
													/>

													{canEditQuotation && (
														<React.Fragment>
															<div
																onClick={onClickAddNewItem}
																className="d-flex flex-row justify-content-center align-items-center p-5 transition-300 bg-hover-kumopack-grey-50 hover-enabled cursor-pointer"
																style={{
																	columnGap: '12px',
																}}>
																<img
																	src="/media/icons/kumopack/circle-add-grey.svg"
																	alt="Circle Add Grey Icon"
																	style={{ userSelect: 'none', pointerEvents: 'none' }}
																/>
																<div className="text-kumopack-grey-900 fw-bold fs-4">
																	เพิ่มรายการใหม่
																</div>
															</div>

															<hr
																style={{
																	background: 'transparent',
																	borderBottom: '1px solid #CCC',
																	borderTop: 0,
																	borderLeft: 0,
																	borderRight: 0,
																	margin: 0,
																}}
															/>
														</React.Fragment>
													)}

													<div className="row py-5 px-5 px-xl-8 gy-5">
														{/* begin:: Table */}
														<div className="d-none d-xl-block col-8" />
														<div className="col-6 col-xl-2 text-end fs-3 fw-bold">ส่วนลด</div>
														<div className="col-6 col-xl-2 text-end fs-3 text-kumopack-grey-400">
															<FormGenerator
																formKey="discount"
																inputType="plain"
																value={financial.discount}
																onChange={e => onChangeDiscount(e.target.value)}
																disabled={isSubmitting || isLoading || !canEditQuotation}
																placeholder="0.00"
																containerClassName=""
																additionalClassName="form-control-sm w-150px ms-auto text-end"
																isHorizontal
															/>
														</div>

														<div className="d-none d-xl-block col-8" />
														<div className="col-6 col-xl-2 text-end fs-3 fw-bold">
															ภาษีมูลค่าเพิ่ม 7%
														</div>
														<div className="col-6 col-xl-2 text-end fs-3 text-kumopack-grey-400 d-flex flex-row align-items-center justify-content-end">
															<input
																name="isRemember"
																className={clsx('d-inline-block form-check-input cursor-pointer', {
																	'bg-kumopack-primary-600 border-kumopack-base-white':
																		financial.isVatCalculated,
																	'border-kumopack-gray-700 bg-kumopack-grey-100':
																		!financial.isVatCalculated,
																})}
																style={{
																	backgroundSize: financial.isVatCalculated ? 'initial' : 'contain',
																}}
																type="checkbox"
																checked={financial.isVatCalculated}
																onChange={e => {
																	onChangeVatCalculated(e.target.checked)
																}}
															/>

															<span className="ms-2 fs-5">
																{/* @ts-ignore */}
																(฿ {formatNumberCommas(vatAmount)})
															</span>
														</div>

														{/* <div className="d-none d-xl-block col-4" />
												<div className="col-6 col-xl-4 text-end fs-3 fw-bold">
													ภาษีที่หัก ณ​ ที่จ่าย
												</div>
												<div className="col-6 col-xl-4 text-end fs-3 text-kumopack-grey-400">
													<FormGenerator
														formKey="vat"
														inputType="plain"
														placeholder="0.00"
														disabled
														// value={financial.discount}
														// onChange={e => onChangeDiscount(e.target.value)}
														containerClassName=""
														additionalClassName="form-control-sm w-150px ms-auto text-end"
														isHorizontal
													/>
												</div> */}
														{/* 
												<div className="d-none d-xl-block col-4" />
												<div className="col-6 col-xl-4 text-end fs-3 fw-bold">ส่วนลด</div>
												<div className="col-6 col-xl-4 text-end fs-3 text-kumopack-grey-400">
													฿ 0.00
												</div> */}
														<div className="d-none d-xl-block col-8" />
														<div className="col-6 col-xl-2 text-end fs-3 fw-bold">
															ยอดรวมยังไม่รวมภาษี
														</div>
														<div className="col-6 col-xl-2 text-end fs-3 fw-bold">
															฿ {!isNaN(netBeforeVat) ? formatNumberCommas(netBeforeVat) : 0.0}
														</div>

														<div className="d-none d-xl-block col-8" />
														<div className="col-6 col-xl-2 text-end fs-3 fw-bold">ยอดรวมสุทธิ</div>
														<div className="col-6 col-xl-2 text-end fs-3 fw-bold">
															฿ {!isNaN(net) ? formatNumberCommas(net) : 0.0}
														</div>
														{/* end:: Table */}
													</div>
												</div>
											</div>
										</div>
									</React.Fragment>
								)}

								{!isSelectingProduct && (
									<div className="col-12">
										<div className="d-flex flex-row justify-content-between">
											<button
												className="btn white-button text-kumopack-grey-700 fw-bold"
												onClick={onBack}
												disabled={isSubmitting}>
												ย้อนกลับ
											</button>

											<div className="d-flex flex-row" style={{ gap: '8px' }}>
												{canGenerateQuotation && (
													<button
														className={clsx(
															'btn fw-bold btn-kumopack-primary-600 text-kumopack-base-white fw-bold',
															{
																// 'white-button text-kumopack-grey-900': canEditQuotation,
																// 'btn-kumopack-primary-600 text-kumopack-base-white fw-bold':
																// 	!canEditQuotation,
															}
														)}
														onClick={onGenerateQuotationForPublic}
														disabled={isSubmitting || isLoading}>
														{!canEditQuotation && (
															<img
																className="me-1"
																src="/media/icons/kumopack/message-chat-circle-white.svg"
																alt="White Message Icon"
																style={{ userSelect: 'none', pointerEvents: 'none' }}
															/>
														)}
														สร้างใบเสนอราคาสำหรับลูกค้า
													</button>
												)}

												{canEditQuotation && (
													<button
														className="btn white-button text-kumopack-grey-900 fw-bold"
														onClick={onSubmit}
														disabled={isSubmitting || isLoading}>
														{/* {!canGenerateQuotation && (
															<img
																className="me-1"
																src="/media/icons/kumopack/circle-add-grey.svg"
																alt="Message Icon"
																width={20}
																height={20}
																style={{ userSelect: 'none', pointerEvents: 'none' }}
															/>
														)} */}
														บันทึก
													</button>
												)}
											</div>
										</div>
									</div>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>
		</ViewPageTitle>
	)
}

/////////////////////
// View Page Title //
/////////////////////

const ViewPageTitle: React.FC<{
	pageTitle: string
	pageBreadCrumb: any[]
	children?: React.ReactNode
}> = ({ pageTitle, pageBreadCrumb, children }) => {
	return (
		<React.Fragment>
			<PageTitle breadcrumbs={pageBreadCrumb}>{pageTitle}</PageTitle>

			{children}
		</React.Fragment>
	)
}

export default DocumentQuotationFormView
